import router from "@/router";
import { useAuthStore } from "@/stores/auth";
import axios from "axios";

const baseURL = process.env.VUE_APP_API_URL;

const instance = axios.create({
  baseURL,
  timeout: 30 * 1000, // timeout in ms
});

// Shared in-flight refresh, so concurrent 401s don't fire N parallel refreshes
let refreshing: Promise<string> | null = null;

async function refreshAccessToken(): Promise<string> {
  const auth = useAuthStore();
  const refresh = auth.refreshToken;
  if (!refresh) throw new Error("No refresh token");

  // Use a bare axios call (not `instance`) so this request bypasses our
  // own response interceptor and can't recurse on refresh failure.
  const { data } = await axios.post(`${baseURL}/token/refresh/`, { refresh });
  auth.setAccessToken(data.access);
  return data.access;
}

async function forceLogout() {
  try {
    await useAuthStore().logout();
  } catch (logoutError) {
    console.error("Error during logout:", logoutError);
  }
  router.push({ name: "login" });
}

// Routes that authenticate or create the user — must NEVER carry an
// Authorization header (a stale token from localStorage would otherwise
// be sent and rejected by the API).
const PUBLIC_ROUTES: Array<{ method: string; pathSuffix: string }> = [
  { method: "post", pathSuffix: "/users/" }, // registration
  { method: "post", pathSuffix: "/token/" }, // login
  { method: "post", pathSuffix: "/token/refresh/" }, // refresh
];

const isPublicRoute = (method: string | undefined, url: string | undefined): boolean => {
  if (!method || !url) return false;
  const m = method.toLowerCase();
  return PUBLIC_ROUTES.some((r) => r.method === m && url.endsWith(r.pathSuffix));
};

// Request interceptor to automatically add auth headers
instance.interceptors.request.use(
  (config) => {
    const token = useAuthStore().accessToken;
    if (token && !isPublicRoute(config.method, config.url)) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor: on 401, transparently refresh the access token and retry once.
instance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config;
    const responseURL: string = error.request?.responseURL ?? "";
    const isAuthEndpoint = responseURL.endsWith("/token/") || responseURL.endsWith("/token/refresh/");

    if (error.response?.status === 401 && !isAuthEndpoint && original && !original._retried) {
      original._retried = true;
      try {
        if (!refreshing) {
          refreshing = refreshAccessToken().finally(() => {
            refreshing = null;
          });
        }
        const newAccess = await refreshing;
        original.headers = original.headers ?? {};
        original.headers.Authorization = `Bearer ${newAccess}`;
        return instance(original);
      } catch (refreshError) {
        await forceLogout();
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

export default instance;
